import axios from 'axios';
import {
  getDesignerApiBaseUrl,
  getEmbeddedDesignerSessionToken,
  isEmbeddedDesignerRuntime,
  setEmbeddedDesignerSessionToken
} from '../runtime/designerRuntime';

const BASE_URI = getDesignerApiBaseUrl() || 'http://localhost:8600/api';
//process.env.NX_PUBLIC_API_BASE_URL || 'http://localhost:8600/api';

const COOKIE_DOMAIN = process.env.NX_PUBLIC_COOKIE_DOMAIN;

/** Signed ?s= session from CRM; used as Authorization: Designer-Session … */
export const DESIGNER_EMBED_SESSION_KEY = 'designer-embed-session';
export const DESIGNER_SESSION_AUTH_SCHEME = 'Designer-Session';
const API_KEY_STORAGE_KEY = 'Api-Key';

export function getDesignerEmbedSession(): string | null {
  const embeddedToken = getEmbeddedDesignerSessionToken();
  if (embeddedToken !== undefined) return embeddedToken;
  const value = localStorage.getItem(DESIGNER_EMBED_SESSION_KEY);
  if (!value || value === 'null') return null;
  return value;
}

export const DESIGNER_EMBED_SESSION_CHANGED = 'designer-embed-session-changed';

export function setDesignerEmbedSession(token: string): void {
  if (isEmbeddedDesignerRuntime()) {
    setEmbeddedDesignerSessionToken(token);
  } else {
    localStorage.setItem(DESIGNER_EMBED_SESSION_KEY, token);
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(DESIGNER_EMBED_SESSION_CHANGED));
  }
}

export function clearDesignerEmbedSession(): void {
  if (isEmbeddedDesignerRuntime()) setEmbeddedDesignerSessionToken(null);
  else localStorage.removeItem(DESIGNER_EMBED_SESSION_KEY);
}

/** True when fetch was aborted (e.g. React StrictMode remount), not a real failure. */
export function isFetchAbortedError(error: unknown): boolean {
  if (error == null) return false;
  if (typeof error === 'object') {
    const e = error as { name?: string; code?: number; message?: string };
    if (e.name === 'AbortError') return true;
    if (e.code === 20) return true;
    const msg = String(e.message ?? '').toLowerCase();
    if (msg.includes('abort') || msg.includes('aborted')) return true;
  }
  return false;
}

function getApiKeyFromStorage(): string | null {
  const key = localStorage.getItem(API_KEY_STORAGE_KEY);
  if (!key || key === 'null') return null;
  return key;
}

/**
 * REST / GraphQL Authorization header for the embedded designer host (Lite).
 * Priority mirrors `resolveProductionAuthHeader` in designer-ui/designer:
 *   1. Designer-Session — signed `?s=` embed token from the CRM
 *   2. Token            — the host's own CRM session, read from this origin's
 *                          localStorage or the shared `.aerialytic.ai` cookie
 *                          (`getSecureToken`). This is Lite's own session,
 *                          never a Portal-origin cookie copied over.
 *   3. Api-Key          — dev / partner override
 */
export function getRestAuthorizationHeader(): string | null {
  const embed = getDesignerEmbedSession();
  if (embed) {
    return `${DESIGNER_SESSION_AUTH_SCHEME} ${embed}`;
  }
  const token = getSecureToken();
  if (token) {
    return `Token ${token}`;
  }
  const apiKey = getApiKeyFromStorage();
  if (apiKey) {
    return `Api-Key ${apiKey}`;
  }
  return null;
}

// Check if development mode is enabled via environment variable or localStorage fallback
const DEVELOPMENT_MODE =
  process.env.NX_PUBLIC_DEVELOPMENT_MODE === 'true' ||
  localStorage.getItem('DEVELOPMENT_MODE') === 'ON';

console.log('🔧 Designer App - BASE_URI:', BASE_URI);
console.log(
  '🔧 Designer App - NX_PUBLIC_API_BASE_URL:',
  process.env.NX_PUBLIC_API_BASE_URL
);
console.log('🔧 Designer App - DEVELOPMENT_MODE:', DEVELOPMENT_MODE);

export const client = axios.create({
  baseURL: BASE_URI,
  timeout: 180000,
  headers: {
    'Content-Type': 'application/json'
  }
});

export function getCookie(name: string): string | null {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = cookies.length - 1; i >= 0; i--) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === name + '=') {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// Secure token retrieval with cross-subdomain fallback (same as installation-settings)
export function getSecureToken(): string | null {
  // First try localStorage (same domain)
  let token = localStorage.getItem('avery-refresh');

  // Fallback to domain cookie (cross-subdomain)
  if (!token) {
    token = getCookie('avery-refresh');

    if (!token) {
      token = getCookie('session'); // This picks up CRM session token!
    }

    if (token) {
      // Cache in localStorage for performance
      localStorage.setItem('avery-refresh', token);
    }
  }

  return token;
}

client.interceptors.request.use((config) => {
  if (config.data instanceof FormData) {
    config.headers['Content-Type'] = 'multipart/form-data';
  }

  if (!config.headers?.Authorization) {
    const authorization = getRestAuthorizationHeader();
    if (authorization) {
      config.headers['Authorization'] = authorization;
    }
  }

  // Skip CSRF only for same-origin localhost API calls. If the request URL is absolute
  // (e.g. designer export to portal.staging), treat as remote and send CSRF when present.
  const urlStr = typeof config.url === 'string' ? config.url : '';
  const resolvedBase =
    (typeof config.baseURL === 'string' && config.baseURL) ||
    (typeof client.defaults.baseURL === 'string' && client.defaults.baseURL) ||
    BASE_URI;
  const target =
    urlStr.startsWith('http://') || urlStr.startsWith('https://')
      ? urlStr
      : `${String(resolvedBase).replace(/\/$/, '')}/${urlStr.replace(/^\//, '')}`;

  const isLocalhost =
    target.includes('localhost') || target.includes('127.0.0.1');

  // Only send CSRF when the API origin matches the page origin. Cross-origin calls
  // (e.g. localhost:4200 → portal.staging) fail CORS if X-CSRFToken is not in
  // Access-Control-Allow-Headers; Token auth does not need CSRF on that path.
  if (!isLocalhost) {
    const csrfToken = getCookie('csrftoken');
    if (csrfToken) {
      let sameOriginAsPage = false;
      if (typeof window !== 'undefined') {
        try {
          sameOriginAsPage = new URL(target).origin === window.location.origin;
        } catch {
          sameOriginAsPage = false;
        }
      } else {
        sameOriginAsPage = true;
      }
      if (sameOriginAsPage) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
    }
  }
  return config;
});

// Add response interceptor for better error handling
client.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log connection errors for debugging
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
      console.error('❌ Designer App - Network connection error:', error.code);
    } else if (error.response?.status === 404) {
      console.error('❌ Designer App - API endpoint not found (404)');
    } else if (error.response?.status >= 500) {
      console.error('❌ Designer App - Server error:', error.response.status);
    }

    return Promise.reject(error);
  }
);

const AuthManager = () => {
  let token: string | null = null;
  let apiKey: string | null = null;
  const logoutEventName = 'avery-logout';
  const refreshName = 'avery-refresh';

  const getToken = (): string | null => {
    if (token === null) {
      const storedToken = localStorage.getItem(refreshName);
      if (storedToken === 'null') return null;
      token = storedToken;
    }
    return token;
  };

  const setToken = (key: string): void => {
    token = key;
    localStorage.setItem(refreshName, key);

    // Set domain cookie for cross-subdomain access if COOKIE_DOMAIN is configured
    try {
      if (COOKIE_DOMAIN) {
        document.cookie = `${refreshName}=${key}; domain=${COOKIE_DOMAIN}; path=/; max-age=86400; secure; samesite=strict`;
      }
    } catch (error) {
      console.log('Could not set domain cookie:', error);
      // Designer continues working normally with localStorage only
    }
  };

  const ereaseToken = (): void => {
    token = null;
    localStorage.setItem(logoutEventName, Date.now().toString());
    localStorage.setItem(refreshName, 'null');

    // Clear domain cookie if configured
    try {
      if (COOKIE_DOMAIN) {
        document.cookie = `${refreshName}=; domain=${COOKIE_DOMAIN}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      }
    } catch (error) {
      console.log('Could not clear domain cookie:', error);
    }
  };

  const setApiKey = (key: string): void => {
    apiKey = key;
    localStorage.setItem('Api-Key', key);
  };

  const eraseApiKey = (): void => {
    apiKey = null;
    localStorage.setItem('Api-Key', 'null');
  };

  const getApiKey = (): string | null => {
    if (apiKey === null) {
      const storedApiKey = localStorage.getItem('Api-Key');
      if (storedApiKey === 'null') return null;
      apiKey = storedApiKey;
    }
    return apiKey;
  };

  // This listener allows to disconnect another session started in another tab
  window.addEventListener('storage', (event) => {
    if (event.key === logoutEventName) {
      token = null;
    }
  });

  return {
    BASE_URI,
    DEVELOPMENT_MODE,
    getToken,
    setToken,
    ereaseToken,
    getApiKey,
    setApiKey,
    eraseApiKey
  };
};

export default AuthManager;
